/**
 * InviteAcceptPage — Phase 4.
 *
 * Handles the share-link path `/invite/:token`. The invitee must be
 * authenticated; if not, App.tsx captures the token in sessionStorage and
 * routes them to /login first. After login the App redirects back here
 * and we POST /api/invitations/accept.
 *
 * Built only from existing UI primitives. No new design language.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';

import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { acceptInvitationByToken, listMyOrganizations } from '../services/organizationApi';
import { useOrganizations } from '../context/OrganizationContext';

type Status = 'working' | 'success' | 'error';

const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refresh } = useOrganizations();

  const [status, setStatus] = useState<Status>('working');
  const [message, setMessage] = useState<string>('Confirming your invitation…');
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!token) {
      setStatus('error');
      setMessage('This invite link is incomplete. Ask the sender to share it again.');
      return;
    }

    (async () => {
      try {
        const membership = await acceptInvitationByToken(token);
        // Clear any pending token saved during the login redirect.
        try { sessionStorage.removeItem('pendingInviteToken'); } catch {}
        // Refresh the org cache so sidebar + topbar update immediately.
        await refresh();
        // Artist-direct invites (membership=null) grant access to a single
        // artist slug — there is no management company to view, so skip the
        // team-welcome screen and land on the dashboard.
        if (!membership) {
          toast.success('You now have access to this artist');
          navigate('/', { replace: true });
          return;
        }
        setStatus('success');
        setMessage('You now have access to this management company in your account.');
        toast.success('You joined the management company');
      } catch (err: any) {
        const apiMsg = err?.response?.data?.message || '';
        const status = err?.response?.status;
        // If the invite was already accepted (e.g. via /accept-with-signup
        // moments earlier, or the user revisited the link), treat it as
        // success rather than a hard error. The grant already exists.
        if (status === 409 && /already accepted|is accepted/i.test(apiMsg)) {
          try { sessionStorage.removeItem('pendingInviteToken'); } catch {}
          // Fetch fresh role info directly (the closure-captured
          // `organizations` from the context would be stale even after
          // refresh()). For artist-direct invites the user has no
          // management role; route them to the dashboard instead of the
          // team-welcome screen that points at /organizations.
          const fresh = await listMyOrganizations().catch(() => []);
          await refresh().catch(() => {});
          const hasMgmtRole = fresh.some((o) => o.myRole === 'owner' || o.myRole === 'admin');
          if (!hasMgmtRole) {
            toast.success('You already have access to this artist');
            navigate('/', { replace: true });
            return;
          }
          setStatus('success');
          setMessage('You already have access to this management company.');
          return;
        }
        const msg = apiMsg || err?.message || 'Failed to accept invitation';
        setStatus('error');
        setMessage(msg);
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="py-10 text-center space-y-4">
          <div className="flex justify-center">
            {status === 'working' && (
              <Spinner size="xl" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            )}
            {status === 'error' && (
              <AlertCircle className="w-10 h-10 text-destructive" />
            )}
          </div>

          <h1 className="text-lg font-semibold">
            {status === 'working' && 'Confirming your invitation'}
            {status === 'success' && 'Welcome to the team'}
            {status === 'error' && 'Could not accept invitation'}
          </h1>

          <p className="text-sm text-muted-foreground">{message}</p>

          {status !== 'working' && (
            <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
              {status === 'success' && (
                <Button
                  onClick={() => navigate('/organizations')}
                  className="gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  View Management Companies
                </Button>
              )}
              <Button
                variant={status === 'success' ? 'outline' : 'default'}
                onClick={() => navigate('/')}
              >
                Go to Dashboard
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteAcceptPage;
